import { memo, useCallback, useRef, useState, type CSSProperties, type MouseEvent, type PointerEvent } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import type { ScheduleItem } from '../../types/index.js'
import {
  ITEM_STATUS_COLORS,
  ITEM_TYPE_COLORS,
  ITEM_STATUS_LABELS,
  ITEM_TYPE_LABELS,
  formatDurationShort,
  scheduleItemHasMissingMedia,
  scheduleItemTitle,
} from '../../lib/utils.js'
import { Badge } from '../ui/badge.js'
import { ambiguousSeasonAt } from '../../lib/clock-change.js'
import { broadcastTimeOfDay } from '../../lib/broadcast-day.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'
import { hasNoSdiSignal } from '../../lib/liveSignal.js'
import { usePlayoutState } from '../../store/index.js'
import type { LayoutItem } from './useAgendaLayout.js'
import { ITEM_PADDING } from './useAgendaLayout.js'
import { selectModeFromEvent, type GridSelectMode } from './useGridSelection.js'
import type { InfoserviceInsertPlan, InfoserviceResizeEdge } from './infoserviceResize.js'
import type { AddGap } from './gapOverflow.js'
import { agendaEdgeAction } from './edgeAction.js'

interface AgendaBlockProps {
  layoutItem: LayoutItem
  isConflict: boolean
  isSelected: boolean
  /** Appartient à la sélection multiple. Indépendant du panneau de détail. */
  isMultiSelected: boolean
  /** Emporté par le glisser d'un autre membre de la sélection. */
  isGroupDragging?: boolean
  isNext: boolean
  hasGapBefore: boolean
  hasGapAfter: boolean
  onSelect: (item: ScheduleItem, mode: GridSelectMode) => void
  onContextMenu?: (item: ScheduleItem, x: number, y: number) => void
  /**
   * Absent = ajout desarme. C'est le seul interrupteur des boutons « + Ajouter »
   * poses aux bords : le parent decide (cadenas, touche Maj, permission), le
   * bloc se contente de constater qu'il n'a personne a appeler.
   */
  onAddAtTime?: (isoDateString: string, mode: 'START' | 'END', plans?: InfoserviceInsertPlan[], gap?: AddGap) => void
  /** Interdit le glisser-deposer. Ne concerne que lui : voir `onAddAtTime`. */
  dragLocked?: boolean
  /**
   * Manœuvre disponible aux deux intersections de ce bloc, quand rien n'y est
   * libre : un infoservice en amont ou en aval rend du temps et la séquence
   * intermédiaire se décale. Elles remplacent le trou manquant — le bouton d'insertion
   * s'affiche quand même.
   */
  /** `null` = pas une intersection ; tableau vide = intersection sans réserve atteignable. */
  insertBefore?: InfoserviceInsertPlan[] | null
  insertAfter?: InfoserviceInsertPlan[] | null
  /**
   * Trou libre bordant ce bloc, avec les infoservices qui l'encadrent. Sert au
   * bouton « Ajouter » quand l'élément posé dépasse le trou : le surplus se
   * prend alors sur l'un des deux côtés. `null` = pas de trou borné de ce côté.
   */
  gapBefore?: AddGap | null
  gapAfter?: AddGap | null
  /** Bords de la plage logique d'infoservice que ce passage porte. */
  infoserviceResizeEdges?: { start: boolean; end: boolean }
  onResizeInfoservice?: (item: ScheduleItem, edge: InfoserviceResizeEdge, deltaPx: number) => Promise<void> | void
  /** Ce que la poignée ajuste, pour son nom accessible (infoservice par défaut). */
  resizeSubject?: 'INFOSERVICE' | 'LIVE'
}

export const AgendaBlock = memo(function AgendaBlock({
  layoutItem,
  isConflict,
  isSelected,
  isMultiSelected,
  isGroupDragging = false,
  isNext,
  hasGapBefore,
  hasGapAfter,
  onSelect,
  onContextMenu,
  onAddAtTime,
  dragLocked = false,
  insertBefore = null,
  insertAfter = null,
  gapBefore = null,
  gapAfter = null,
  infoserviceResizeEdges,
  onResizeInfoservice,
  resizeSubject = 'INFOSERVICE',
}: AgendaBlockProps) {
  // Les elements trop courts ne passent plus ici : ils sont dans la gouttiere
  // des liaisons (`AgendaMarkerGutter`).
  const { item, top, height, left, width } = layoutItem
  const isDraggable = !['ON_AIR', 'DONE', 'LOADING'].includes(item.status) && !dragLocked
  const [hovered, setHovered] = useState(false)
  const resizeGesture = useRef<{ edge: InfoserviceResizeEdge; startY: number } | null>(null)
  const [resizePreview, setResizePreview] = useState<{ edge: InfoserviceResizeEdge; deltaPx: number } | null>(null)
  const [resizePending, setResizePending] = useState(false)
  const beforeAction = agendaEdgeAction(hasGapBefore, insertBefore)
  const afterAction = agendaEdgeAction(hasGapAfter, insertAfter)
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled: !isDraggable,
    data: { item },
  })

  const colors = item.status === 'SCHEDULED' || item.status === 'LOADING'
    ? ITEM_TYPE_COLORS[item.itemType]
    : ITEM_STATUS_COLORS[item.status]
  const blockHeight = height - 2
  const previewDelta = resizePreview?.deltaPx ?? 0
  const previewTop = resizePreview?.edge === 'START'
    ? Math.min(previewDelta, blockHeight - 4)
    : 0
  const previewHeight = resizePreview?.edge === 'START'
    ? Math.max(4, blockHeight - previewTop)
    : resizePreview?.edge === 'END'
      ? Math.max(4, blockHeight + previewDelta)
      : blockHeight
  const style = {
    '--agenda-block-top': `${top + 1 + previewTop}px`,
    '--agenda-block-height': `${previewHeight}px`,
    '--agenda-block-left': `${left}%`,
    '--agenda-block-width': `${width}%`,
    '--agenda-item-padding': `${ITEM_PADDING}px`,
    '--agenda-block-transform': CSS.Translate.toString(transform) || 'none',
    '--agenda-block-bg': colors.bg,
    '--agenda-block-border': colors.border,
    '--agenda-block-text': colors.text,
    '--agenda-block-badge': colors.badge,
  } as CSSProperties

  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    // Maj + clic étend la sélection : le navigateur en profiterait pour
    // surligner le texte des blocs traversés si on le laissait faire.
    if (event.shiftKey) window.getSelection()?.removeAllRanges()
    onSelect(item, selectModeFromEvent(event))
  }, [item, onSelect])
  const handleContextMenu = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onContextMenu?.(item, event.clientX, event.clientY)
  }, [item, onContextMenu])
  const handleMouseEnter = useCallback(() => setHovered(true), [])
  const handleMouseLeave = useCallback(() => setHovered(false), [])
  const handleAddBefore = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onAddAtTime?.(item.startTime, 'END', undefined, gapBefore ?? undefined)
  }, [item.startTime, onAddAtTime, gapBefore])
  // Insertion « en force » : rien n'est libre à cette intersection, mais un
  // infoservice voisin peut rendre les secondes nécessaires.
  const handleInsertBefore = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onAddAtTime?.(item.startTime, insertBefore?.[0]?.edge ?? 'END', insertBefore ?? [])
  }, [item.startTime, onAddAtTime, insertBefore])
  const handleInsertAfter = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onAddAtTime?.(item.endTime, insertAfter?.[0]?.edge ?? 'END', insertAfter ?? [])
  }, [item.endTime, onAddAtTime, insertAfter])
  const handleAddAfter = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onAddAtTime?.(item.endTime, 'START', undefined, gapAfter ?? undefined)
  }, [item.endTime, onAddAtTime, gapAfter])
  const handleResizePointerDown = useCallback((edge: InfoserviceResizeEdge, event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    resizeGesture.current = { edge, startY: event.clientY }
    setResizePreview({ edge, deltaPx: 0 })
  }, [])
  const handleResizePointerMove = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    const gesture = resizeGesture.current
    if (!gesture) return
    event.preventDefault()
    event.stopPropagation()
    setResizePreview({ edge: gesture.edge, deltaPx: event.clientY - gesture.startY })
  }, [])
  const handleResizePointerUp = useCallback(async (event: PointerEvent<HTMLButtonElement>) => {
    const gesture = resizeGesture.current
    if (!gesture) return
    event.preventDefault()
    event.stopPropagation()
    const deltaPx = event.clientY - gesture.startY
    resizeGesture.current = null
    setResizePreview(null)
    if (Math.abs(deltaPx) < 2 || !onResizeInfoservice) return
    setResizePending(true)
    try {
      await onResizeInfoservice(item, gesture.edge, deltaPx)
    } finally {
      setResizePending(false)
    }
  }, [item, onResizeInfoservice])
  const handleResizePointerCancel = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    resizeGesture.current = null
    setResizePreview(null)
  }, [])

  const classes = [
    'agenda-block',
    `is-type-${item.itemType.toLowerCase().replace('_', '-')}`,
    isDraggable ? 'is-draggable' : '',
    isDragging ? 'is-dragging' : '',
    isGroupDragging ? 'is-group-dragging' : '',
    isSelected ? 'is-selected' : '',
    isMultiSelected ? 'is-multi-selected' : '',
    hovered ? 'is-hovered' : '',
    isConflict ? 'is-conflict' : '',
    item.status === 'ON_AIR' ? 'is-on-air' : '',
    isNext ? 'is-next' : '',
    item.status === 'ERROR' ? 'is-error' : '',
    resizePreview ? 'is-resizing' : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      ref={setNodeRef}
      className={classes}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="agenda-block__surface">
        {item.status === 'ON_AIR' && <span className="agenda-block__on-air">ON AIR</span>}
        <BlockContent item={item} height={blockHeight} />
      </div>

      {infoserviceResizeEdges?.start && onResizeInfoservice && (
        <button
          type="button"
          className="agenda-block__resize-handle is-start"
          aria-label={resizeSubject === 'LIVE' ? 'Ajuster le début du direct' : 'Ajuster le début de l’infoservice'}
          disabled={resizePending}
          onPointerDown={event => handleResizePointerDown('START', event)}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          onPointerCancel={handleResizePointerCancel}
          onClick={event => event.stopPropagation()}
        />
      )}
      {infoserviceResizeEdges?.end && onResizeInfoservice && (
        <button
          type="button"
          className="agenda-block__resize-handle is-end"
          aria-label={resizeSubject === 'LIVE' ? 'Ajuster la fin du direct' : 'Ajuster la fin de l’infoservice'}
          disabled={resizePending}
          onPointerDown={event => handleResizePointerDown('END', event)}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          onPointerCancel={handleResizePointerCancel}
          onClick={event => event.stopPropagation()}
        />
      )}

      {hovered && beforeAction === 'ADD' && onAddAtTime && (
        <button type="button" onClick={handleAddBefore} className="agenda-block__add is-before">+ Ajouter</button>
      )}
      {hovered && beforeAction === 'INSERT' && insertBefore !== null && onAddAtTime && (
        <button
          type="button"
          onClick={handleInsertBefore}
          className="agenda-block__add is-before is-trim"
          title={insertTitle(insertBefore)}
        >+ Insérer</button>
      )}
      {hovered && afterAction === 'INSERT' && insertAfter !== null && onAddAtTime && (
        <button
          type="button"
          onClick={handleInsertAfter}
          className="agenda-block__add is-after is-trim"
          title={insertTitle(insertAfter)}
        >+ Insérer</button>
      )}
      {hovered && afterAction === 'ADD' && onAddAtTime && (
        <button type="button" onClick={handleAddAfter} className="agenda-block__add is-after">+ Ajouter</button>
      )}
    </div>
  )
})

/** Le bouton ne dit pas seulement où l'on insère, mais qui paie la place. */
function insertTitle(plans: InfoserviceInsertPlan[]): string {
  if (plans.length === 0) return 'Insertion impossible : aucun infoservice ajustable n’est atteignable'
  if (plans.length > 1) return 'Insérer ici en choisissant l’infoservice du haut ou du bas'
  const plan = plans[0]
  const side = plan.edge === 'START' ? 'suit' : 'précède'
  const direction = plan.edge === 'START' ? 'reculé' : 'avancé'
  return plan.shift.length === 0
    ? `Insérer ici : l’infoservice qui ${side} sera raccourci d’autant`
    : `Insérer ici : l’infoservice sera raccourci et ${plan.shift.length} élément(s) seront ${direction}(s) d’autant`
}

const BlockContent = memo(function BlockContent({ item, height }: { item: ScheduleItem; height: number }) {
  const adPlacements = item.adPlacements ?? []
  const adSpotCount = adPlacements.length
  const adTitle = item.itemType === 'AD_BREAK'
    ? 'Bloc Pubs'
    : item.itemType === 'INFOSERVICE'
      ? adPlacements[0]?.spot.title ?? 'Infoservice'
      : null
  const baseTitle = scheduleItemTitle(item, adTitle ?? 'Item') ?? 'Item'
  const title = item.bundleId && item.program
    ? `${baseTitle} · Partie ${item.program.partNumber ?? 1}`
    : baseTitle
  const typeLabel = ITEM_TYPE_LABELS[item.itemType]
  const statusLabel = ITEM_STATUS_LABELS[item.status]
  const duration = formatDurationShort(item.durationSec)
  const startTime = broadcastTimeOfDay(new Date(item.startTime))
  const endTime = broadcastTimeOfDay(new Date(item.endTime))
  const startSeason = ambiguousSeasonAt(item.startTime)
  const endSeason = ambiguousSeasonAt(item.endTime)
  const clockTitle = `${startTime}${startSeason ? ` ${startSeason}` : ''} → ${endTime}${endSeason ? ` ${endSeason}` : ''}`
  const isMultiSpotBlock = item.itemType === 'AD_BREAK' && adSpotCount > 1
  const meta = item.itemType === 'AD_BREAK'
    ? `${typeLabel} · ${adSpotCount} spot${adSpotCount === 1 ? '' : 's'} · ${duration}`
    : `${typeLabel} · ${duration}`
  const hasMissingMedia = scheduleItemHasMissingMedia(item)

  // « Pas de signal » n'a de sens que sur le direct réellement à l'antenne :
  // la sonde décrit l'entrée SDI en cours, pas celle d'un direct de demain.
  const playoutState = usePlayoutState()
  const hasNoSignal = item.itemType === 'LIVE'
    && item.status === 'ON_AIR'
    && hasNoSdiSignal(playoutState?.casparStatus)

  if (height < 14) {
    return <div className="agenda-block-content is-tiny" title={clockTitle}><span className="agenda-block__title">{title}</span></div>
  }

  if (height < 26) {
    return (
      <div className="agenda-block-content is-compact" title={clockTitle}>
        {item.itemType === 'LIVE' && <LiveDot active={item.status === 'ON_AIR'} />}
        {item.itemType === 'JINGLE' && <span className="agenda-block__symbol" aria-hidden="true">♪</span>}
        {item.recurrenceId && <span className="agenda-block__symbol" title="Récurrent">↻</span>}
        <span className="agenda-block__title">{title}</span>
        <span className="agenda-block__duration">{duration}</span>
      </div>
    )
  }

  if ((startSeason || endSeason) && height < 38) {
    return (
      <div className="agenda-block-content is-compact is-clock-compact" title={clockTitle}>
        <span className="agenda-block__title">
          {item.itemType === 'LIVE' && <LiveDot active={item.status === 'ON_AIR'} />}
          {item.itemType === 'JINGLE' && <span className="agenda-block__symbol" aria-hidden="true">♪</span>}
          {item.recurrenceId && <span className="agenda-block__symbol" title="Récurrent">↻</span>}
          {title}
        </span>
        <span className="agenda-block__times">
          {startSeason ? startTime : `→ ${endTime}`}<strong className="agenda-block__season"> {startSeason ?? endSeason}</strong>
        </span>
      </div>
    )
  }

  const hasAmbiguousTime = Boolean(startSeason || endSeason)
  const showSub = height >= 38 && !(hasAmbiguousTime && height < 78)
  const showBadge = height >= 52
  const showTimes = height >= 64 || hasAmbiguousTime

  return (
    <div className={`agenda-block-content${hasAmbiguousTime ? ' has-clock-time' : ''}`} title={clockTitle}>
      <div className="agenda-block__title">
        {item.itemType === 'LIVE' && <LiveDot active={item.status === 'ON_AIR'} />}
        {item.itemType === 'JINGLE' && <span className="agenda-block__symbol" aria-hidden="true">♪</span>}
        {item.recurrenceId && <span className="agenda-block__symbol" title="Événement récurrent">↻</span>}
        {title}
      </div>
      {showSub && <div className="agenda-block__meta">{meta}</div>}
      {isMultiSpotBlock && height >= 80 && (
        <div className="agenda-block__spots">
          {adPlacements.slice(0, 4).map((placement, index) => (
            <div key={placement.id} className="agenda-block__spot">{index + 1}. {formatAdSpotLabel(placement.spot)} · {placement.spot.durationSec}s</div>
          ))}
          {adPlacements.length > 4 && <div className="agenda-block__spot-more">+ {adPlacements.length - 4} autre(s)</div>}
        </div>
      )}
      {showBadge && (
        <div className="agenda-block__badges">
          <Badge variant="outline" className="agenda-block__badge">
            {item.status === 'ON_AIR' ? 'ANTENNE' : statusLabel}
          </Badge>
          {hasMissingMedia && (
            <Badge
              variant="destructive"
              className="agenda-block__badge is-missing"
              title="Aucun média prêt à être diffusé n’est associé à ce bloc"
            >
              Média manquant
            </Badge>
          )}
          {hasNoSignal && (
            <Badge
              variant="destructive"
              className="agenda-block__badge is-missing"
              title="Le playout ne reçoit aucun signal sur l’entrée SDI : ce direct partirait au noir"
            >
              Pas de signal
            </Badge>
          )}
        </div>
      )}
      {showTimes && (
        <div className="agenda-block__times">
          <span>{startTime}{startSeason && <strong className="agenda-block__season"> {startSeason}</strong>}</span>
          <span>{endTime}{endSeason && <strong className="agenda-block__season"> {endSeason}</strong>}</span>
        </div>
      )}
    </div>
  )
})

const LiveDot = memo(function LiveDot({ active }: { active: boolean }) {
  return <span className={`agenda-block__live-dot${active ? ' is-pulsing' : ''}`} aria-hidden="true" />
})

export default AgendaBlock
